import { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, Save, X, Eye, FileText, Tag, CheckCircle2, FileSpreadsheet, Search, Printer } from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../contexts/ToastContext';
import Skeleton from './Skeleton';
import { ZebraPrinter } from '../utils/zebraPrinter';

interface Envio3PL {
  id: number;
  codigo: string;
  fecha_objetivo: string;
  estado: 'FASE_MONTAJE' | 'ENVIADO';
  created_at: string;
  updated_at: string;
  created_by: number | null;
  lines: Envio3PLLine[];
}

interface Envio3PLLine {
  id: number;
  envio_3pl_id: number;
  sku: string;
  nombre_producto: string;
  cantidad: number;
  is_completed?: boolean;
  created_at: string;
  updated_at: string;
}

export default function ThreePL() {
  const { api } = useApi();
  const { showSuccess, showError } = useToast();

  const [loadingAutoGenerate, setLoadingAutoGenerate] = useState(false);

  // Estado para envíos
  const [envios, setEnvios] = useState<Envio3PL[]>([]);
  const [loadingEnvios, setLoadingEnvios] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showStockMovementsModal, setShowStockMovementsModal] = useState(false);
  const [stockMovements, setStockMovements] = useState<Array<{
    cod_pieza: string;
    nombre_pieza: string;
    location_code: string;
    qty_needed: number;
    qty_deducted: number;
    stock_before: number;
    stock_after: number;
  }>>([]);
  const [failedMovements, setFailedMovements] = useState<Array<{
    cod_pieza: string;
    nombre_pieza: string;
    location_code: string;
    qty_needed: number;
    reason: string;
  }>>([]);
  const [selectedEnvio, setSelectedEnvio] = useState<Envio3PL | null>(null);
  const [bomItems, setBomItems] = useState<Array<{
    cod_pieza: string;
    nombre_pieza: string;
    cantidad_necesaria: number;
    stock_acabado: boolean;
    stock_taller: boolean;
    pedir_fundicion: boolean;
    cantidad_fundicion: number;
    acabados_necesarios?: Array<{ acabado: string; cantidad: number }>;
  }>>([]);
  const [loadingBOM, setLoadingBOM] = useState(false);
  const [fechaObjetivo, setFechaObjetivo] = useState<string>('');
  const [editingLineId, setEditingLineId] = useState<number | null>(null);
  const [editingLineQuantity, setEditingLineQuantity] = useState<number>(0);
  const [showAddLineModal, setShowAddLineModal] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<Array<{
    id: number;
    sku: string;
    title: string;
    product_title: string;
    price: string;
  }>>([]);
  const [searchingProducts, setSearchingProducts] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<{
    id: number;
    sku: string;
    title: string;
    product_title: string;
    price: string;
  } | null>(null);
  const [newLineQuantity, setNewLineQuantity] = useState<number>(1);

  // Cargar envíos al montar el componente
  useEffect(() => {
    loadEnvios();
  }, []);

  // Búsqueda automática de productos con debounce
  useEffect(() => {
    if (!showAddLineModal) {
      return;
    }

    const trimmedQuery = productSearchQuery.trim();

    // Si el query está vacío, limpiar resultados
    if (!trimmedQuery) {
      setSearchResults([]);
      setSelectedVariant(null);
      return;
    }

    // Debounce: esperar 500ms después de que el usuario deje de escribir
    const timeoutId = setTimeout(() => {
      handleSearchProducts();
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearchQuery, showAddLineModal]);

  const loadEnvios = async () => {
    try {
      setLoadingEnvios(true);
      const response = await api.get('/3pl/envios');
      setEnvios(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar envíos');
    } finally {
      setLoadingEnvios(false);
    }
  };

  const handleUpdateEstado = async (envioId: number, nuevoEstado: 'FASE_MONTAJE' | 'ENVIADO') => {
    try {
      const response = await api.put(`/3pl/envios/${envioId}/estado`, { estado: nuevoEstado });
      showSuccess('Estado actualizado correctamente');
      loadEnvios();

      // Si se marcó como ENVIADO y hay movimientos de stock o fallos, mostrar modal
      if (nuevoEstado === 'ENVIADO') {
        const movements = response.data?.stockMovements || [];
        const failed = response.data?.failedMovements || [];

        if (movements.length > 0 || failed.length > 0) {
          setStockMovements(movements);
          setFailedMovements(failed);
          setShowStockMovementsModal(true);
        }
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar estado');
    }
  };

  const handleDeleteEnvio = async (envioId: number) => {
    if (!confirm('¿Estás seguro de eliminar este envío?')) {
      return;
    }

    try {
      await api.delete(`/3pl/envios/${envioId}`);
      showSuccess('Envío eliminado correctamente');
      loadEnvios();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar envío');
    }
  };

  const handleViewEnvio = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}`);
      setSelectedEnvio(response.data);
      setShowDetailModal(true);

      // Cargar BOM del envío
      setLoadingBOM(true);
      try {
        const bomResponse = await api.get(`/3pl/envios/${envioId}/bom`);
        setBomItems(bomResponse.data);
      } catch (bomErr: any) {
        // Error silencioso - no crítico para la funcionalidad
        setBomItems([]);
      } finally {
        setLoadingBOM(false);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar envío');
    }
  };

  const handleStartEditLine = (line: Envio3PLLine) => {
    setEditingLineId(line.id);
    setEditingLineQuantity(line.cantidad);
  };

  const handleCancelEditLine = () => {
    setEditingLineId(null);
    setEditingLineQuantity(0);
  };

  const handleSaveLineQuantity = async (lineId: number) => {
    try {
      await api.put(`/3pl/envios/lines/${lineId}/quantity`, {
        cantidad: editingLineQuantity,
      });
      showSuccess('Cantidad actualizada correctamente');
      setEditingLineId(null);
      // Recargar envío
      if (selectedEnvio) {
        await handleViewEnvio(selectedEnvio.id);
      }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar cantidad');
    }
  };

  const handleDeleteEnvioLine = async (lineId: number) => {
    console.log('Borrado de línea:', lineId);

    // Guardar estado previo para posible reversión
    const previousEnvio = selectedEnvio ? { ...selectedEnvio } : null;

    // Actualización optimista: quitar la línea del estado inmediatamente
    if (selectedEnvio) {
      setSelectedEnvio({
        ...selectedEnvio,
        lines: selectedEnvio.lines.filter(l => Number(l.id) !== Number(lineId))
      });
    }

    try {
      await api.delete(`/3pl/envios/lines/${lineId}`);
      showSuccess('Línea eliminada correctamente');

      // Recargar del servidor para sincronía total y actualizar BOM
      if (previousEnvio) {
        await handleViewEnvio(previousEnvio.id);
      }
      loadEnvios();
    } catch (err: any) {
      console.error('❌ Error fatal al borrar:', err);
      // Revertir cambio optimista
      if (previousEnvio) {
        setSelectedEnvio(previousEnvio);
      }
      const errorMsg = err.response?.data?.error || err.message;
      showError('No se pudo eliminar: ' + errorMsg);
    }
  };

  const handleToggleLineCompletion = async (lineId: number, isCompleted: boolean) => {
    try {
      await api.put(`/3pl/envios/lines/${lineId}/complete`, {
        is_completed: isCompleted,
      });
      showSuccess(`Línea ${isCompleted ? 'completada' : 'pendiente'}`);
      
      // Actualización optimista del estado local
      if (selectedEnvio) {
        const updatedLines = selectedEnvio.lines.map(l => 
          l.id === lineId ? { ...l, is_completed: isCompleted } : l
        );
        setSelectedEnvio({
          ...selectedEnvio,
          lines: updatedLines
        });
      }
    } catch (err: any) {
      console.error('Error completando línea:', err);
      showError(err.response?.data?.error || 'Error al actualizar progreso de la línea');
    }
  };

  const handleSearchProducts = async () => {
    const trimmedQuery = productSearchQuery.trim();
    if (!trimmedQuery) {
      setSearchResults([]);
      return;
    }

    try {
      setSearchingProducts(true);
      const response = await api.get(`/3pl/products/search?query=${encodeURIComponent(trimmedQuery)}`);
      setSearchResults(response.data.variants || []);
    } catch (err: any) {
      console.error('Error buscando productos:', err);
      // No mostrar error si el usuario está escribiendo (evitar spam de errores)
      if (trimmedQuery.length >= 2) {
        showError(err.response?.data?.error || 'Error al buscar productos');
      }
      setSearchResults([]);
    } finally {
      setSearchingProducts(false);
    }
  };

  const handleAddLine = async () => {
    if (!selectedEnvio || !selectedVariant || newLineQuantity <= 0) {
      showError('Por favor selecciona un producto y especifica una cantidad');
      return;
    }

    try {
      await api.post(`/3pl/envios/${selectedEnvio.id}/lines`, {
        sku: selectedVariant.sku,
        nombre_producto: `${selectedVariant.product_title} - ${selectedVariant.title}`,
        cantidad: newLineQuantity,
      });
      showSuccess('Línea añadida correctamente');
      setShowAddLineModal(false);
      setSelectedVariant(null);
      setProductSearchQuery('');
      setSearchResults([]);
      setNewLineQuantity(1);
      // Recargar envío
      await handleViewEnvio(selectedEnvio.id);
      // Recargar lista de envíos
      loadEnvios();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al añadir línea');
    }
  };

  const handlePrintPDF = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}/pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      iframe.src = url;

      document.body.appendChild(iframe);

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.print();
            setTimeout(() => {
              document.body.removeChild(iframe);
              window.URL.revokeObjectURL(url);
            }, 1000);
          } catch (error) {
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
              setTimeout(() => {
                printWindow.print();
              }, 500);
            }
            document.body.removeChild(iframe);
          }
        }, 500);
      };

      setTimeout(() => {
        if (iframe.parentNode) {
          try {
            iframe.contentWindow?.print();
            setTimeout(() => {
              if (iframe.parentNode) {
                document.body.removeChild(iframe);
              }
              window.URL.revokeObjectURL(url);
            }, 1000);
          } catch (error) {
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
              setTimeout(() => {
                printWindow.print();
              }, 500);
            }
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          }
        }
      }, 2000);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al generar PDF');
    }
  };

  const handlePrintLabels = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}/labels`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);

      // Crear un enlace temporal para descargar el archivo ZPL
      const link = document.createElement('a');
      link.href = url;
      link.download = `etiquetas-envio-3pl-${envioId}.zpl`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Archivo ZPL descargado. Puedes enviarlo a la impresora Zebra.');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al generar etiquetas');
    }
  };

  const handlePrintLabelsDirect = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}/labels`);
      const zpl = response.data;

      if (!zpl) {
        showError('No se pudo obtener el código ZPL del servidor.');
        return;
      }

      await ZebraPrinter.printZPL(zpl);
      showSuccess('Comando de impresión enviado a la Zebra correctamente.');
    } catch (err: any) {
      console.error('Error en impresión directa:', err);
      showError(err.message || 'Error al imprimir directamente. ¿Está Zebra Browser Print instalado?');
    }
  };

  const handlePrintLabelsPDF = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}/labels/pdf`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Abrir PDF en nueva ventana para imprimir
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      } else {
        // Si no se puede abrir, descargar
        const link = document.createElement('a');
        link.href = url;
        link.download = `etiquetas-envio-3pl-${envioId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      showSuccess('PDF de etiquetas generado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al generar etiquetas PDF');
    }
  };

  const handleDownloadCSV = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}/csv`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      // Crear un enlace temporal para descargar el archivo CSV
      const link = document.createElement('a');
      link.href = url;
      link.download = `amphora-envio-3pl-${envioId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('CSV descargado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descargar CSV');
    }
  };

  const handleDownloadAmphoraCSV = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}/csv`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `amphora-envio-${envioId}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('CSV de Amphora descargado correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descargar CSV de Amphora');
    }
  };

  const handleDownloadCommercialInvoice = async (envioId: number) => {
    try {
      const response = await api.get(`/3pl/envios/${envioId}/commercial-invoice`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      const dateObj = new Date();
      const dateStr = `${('0' + dateObj.getDate()).slice(-2)}${('0' + (dateObj.getMonth() + 1)).slice(-2)}${dateObj.getFullYear()}`;
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `SKRS-${dateStr} - Commercial Invoice.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess('Factura comercial descargada correctamente');
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al descargar Factura Comercial');
    }
  };

  const handleAutoGenerateEnvio = async () => {
    if (!confirm('¿Estás seguro de que quieres auto-generar un envío basado en las ventas de los últimos 7 días?')) {
      return;
    }

    try {
      setLoadingAutoGenerate(true);

      // Calcular fechas: últimos 7 días hasta hoy
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 7);

      const startDateStr = start.toISOString().split('T')[0];
      const endDateStr = end.toISOString().split('T')[0];

      // 1. Obtener ventas
      const params = new URLSearchParams({
        startDate: startDateStr,
        endDate: endDateStr,
      });

      const response = await api.get(`/3pl/sales?${params}`);
      const salesData: { sku: string; nombre_producto: string; cantidad_vendida: number; precio: number }[] = response.data;

      // 2. Filtrar como en applyFilters por defecto y quitar negativos/0
      const defaultTypes = ['pulseras', 'collares', 'anillos', 'colgantes', 'servicios'];
      let filtered = salesData.filter(sale => {
        if (sale.cantidad_vendida <= 0) return false;

        const productNameLower = sale.nombre_producto.toLowerCase();
        const isService = sale.precio < 100;

        if (isService && defaultTypes.includes('servicios')) return true;
        if (!isService) {
          if (defaultTypes.includes('pulseras') && (productNameLower.includes('bracelet') || productNameLower.includes('pulsera'))) return true;
          if (defaultTypes.includes('collares') && (productNameLower.includes('necklace') || productNameLower.includes('collar'))) return true;
          if (defaultTypes.includes('anillos') && (productNameLower.includes('ring') || productNameLower.includes('anillo'))) return true;
          if (defaultTypes.includes('colgantes') && (productNameLower.includes('pendant') || productNameLower.includes('colgante'))) return true;
        }
        return false;
      });

      if (filtered.length === 0) {
        showError('No hay ventas válidas en los últimos 7 días con los filtros por defecto.');
        return;
      }

      // 3. Preparar líneas: Multiplicando las unidades vendidas por 1.30 y redondeando hacia arriba
      const linesToSend = filtered.map(s => ({
        sku: s.sku,
        nombre_producto: s.nombre_producto,
        cantidad: Math.ceil(s.cantidad_vendida * 1.30),
      }));

      // 4. Calcular fecha objetivo (7 días en el futuro)
      const objectiveDate = new Date();
      objectiveDate.setDate(objectiveDate.getDate() + 7);
      const objectiveDateStr = objectiveDate.toISOString().split('T')[0];

      // 5. Crear envío
      await api.post('/3pl/envios', {
        fecha_objetivo: objectiveDateStr,
        lines: linesToSend,
      });

      showSuccess('Envío auto-generado correctamente');

      // Recargar envíos
      loadEnvios();
    } catch (err: any) {
      console.error('Error auto-generando envío:', err);
      showError(err.response?.data?.error || 'Error al auto-generar el envío');
    } finally {
      setLoadingAutoGenerate(false);
    }
  };

  const handleCreateManualEnvio = async () => {
    try {
      setLoadingAutoGenerate(true);

      // Calcular fecha objetivo (7 días en el futuro por defecto)
      const objectiveDate = new Date();
      objectiveDate.setDate(objectiveDate.getDate() + 7);
      const objectiveDateStr = objectiveDate.toISOString().split('T')[0];

      await api.post('/3pl/envios', {
        fecha_objetivo: objectiveDateStr,
        lines: [],
      });

      showSuccess('Envío manual vacío creado correctamente');
      loadEnvios();
    } catch (err: any) {
      console.error('Error creando envío manual:', err);
      showError(err.response?.data?.error || 'Error al crear envío manual');
    } finally {
      setLoadingAutoGenerate(false);
    }
  };

  return (
    <div className="tab-content">
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
          <h3>Envíos 3PL</h3>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', marginBottom: '1rem' }}>
            <button
              className="btn btn-secondary"
              onClick={handleCreateManualEnvio}
              disabled={loadingAutoGenerate}
            >
              <Plus size={16} /> Crear Envío Manual (Vacío)
            </button>
            <button
              className="btn btn-primary"
              onClick={handleAutoGenerateEnvio}
              disabled={loadingAutoGenerate}
            >
              <Plus size={16} /> {loadingAutoGenerate ? 'Generando...' : 'Auto-generar Envío (Últimos 7 días)'}
            </button>
          </div>
          {loadingEnvios ? (
            <Skeleton height="400px" variant="rounded" />
          ) : envios.length === 0 ? (
            <div className="empty-state">
              <p>No hay envíos registrados</p>
            </div>
          ) : (
            <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>Fecha Objetivo</th>
                      <th>Estado</th>
                      <th style={{ textAlign: 'center' }}>Progreso</th>
                      <th style={{ textAlign: 'center' }}>Total Cantidad</th>
                      <th>Fecha Creación</th>
                      <th style={{ textAlign: 'center' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {envios.map((envio) => (
                      <tr key={envio.id}>
                        <td><strong>{envio.codigo}</strong></td>
                        <td>{new Date(envio.fecha_objetivo).toLocaleDateString('es-ES')}</td>
                        <td>
                          <span
                            style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.85rem',
                              backgroundColor: envio.estado === 'ENVIADO' ? 'var(--accent)' : 'var(--warning)',
                              color: 'white',
                            }}
                          >
                            {envio.estado === 'FASE_MONTAJE' ? 'Montaje' : 'Enviado'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem' }}>
                            <span style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                              {envio.lines.reduce((sum, line) => sum + (line.is_completed ? line.cantidad : 0), 0)} / {envio.lines.reduce((sum, line) => sum + line.cantidad, 0)} u.
                            </span>
                            <div style={{ width: '80px', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px', height: '6px', overflow: 'hidden', border: '1px solid var(--border-color)', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.1)' }}>
                              <div
                                style={{
                                  height: '100%',
                                  backgroundColor: envio.estado === 'ENVIADO' ? 'var(--accent)' : '#22c55e',
                                  width: `${Math.round((envio.lines.reduce((sum, line) => sum + (line.is_completed ? line.cantidad : 0), 0) / Math.max(1, envio.lines.reduce((sum, line) => sum + line.cantidad, 0))) * 100)}%`,
                                  transition: 'width 0.4s ease-out'
                                }}
                              />
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          <strong>{envio.lines.reduce((sum, line) => sum + line.cantidad, 0)}</strong>
                        </td>
                        <td>{new Date(envio.created_at).toLocaleDateString('es-ES')}</td>
                        <td style={{ textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleViewEnvio(envio.id)}
                              title="Ver detalle"
                            >
                              <Eye size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handlePrintPDF(envio.id)}
                              title="Imprimir PDF"
                            >
                              <FileText size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handlePrintLabelsDirect(envio.id)}
                              title="Imprimir ZPL Directo (Zebra)"
                            >
                              <Printer size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handlePrintLabels(envio.id)}
                              title="Imprimir etiquetas ZPL"
                            >
                              <Tag size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handlePrintLabelsPDF(envio.id)}
                              title="Descargar etiquetas PDF"
                            >
                              <FileText size={14} />
                            </button>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleDownloadAmphoraCSV(envio.id)}
                              title="Descargar CSV para Amphora"
                            >
                              <FileSpreadsheet size={14} />
                            </button>
                            {envio.estado === 'FASE_MONTAJE' && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleUpdateEstado(envio.id, 'ENVIADO')}
                                title="Marcar como enviado"
                              >
                                Marcar Enviado
                              </button>
                            )}
                            {envio.estado === 'ENVIADO' && (
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleUpdateEstado(envio.id, 'FASE_MONTAJE')}
                                title="Volver a fase de montaje"
                              >
                                Volver a Montaje
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDeleteEnvio(envio.id)}
                              title="Eliminar envío"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        {/* Modal para ver detalle del envío */}
        {showDetailModal && selectedEnvio && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onClick={() => setShowDetailModal(false)}
          >
            <div
              style={{
                backgroundColor: 'var(--bg-primary)',
                padding: '2rem',
                borderRadius: '8px',
                maxWidth: '800px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                border: '1px solid var(--border-color)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0 }}>Detalle del Envío: {selectedEnvio.codigo}</h3>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowDetailModal(false)}
                >
                  <X size={16} />
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                  <div>
                    <strong>Fecha Objetivo:</strong> {new Date(selectedEnvio.fecha_objetivo).toLocaleDateString('es-ES')}
                  </div>
                  <div>
                    <strong>Estado:</strong>{' '}
                    <span
                      style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        backgroundColor: selectedEnvio.estado === 'ENVIADO' ? 'var(--accent)' : 'var(--warning)',
                        color: 'white',
                      }}
                    >
                      {selectedEnvio.estado === 'FASE_MONTAJE' ? 'Fase de Montaje' : 'Enviado'}
                    </span>
                  </div>
                  <div>
                    <strong>Fecha Creación:</strong> {new Date(selectedEnvio.created_at).toLocaleDateString('es-ES')}
                  </div>
                  <div>
                    <strong>Total Productos:</strong> {selectedEnvio.lines.length}
                  </div>
                  <div>
                    <strong>Total Cantidad:</strong> {selectedEnvio.lines.reduce((sum, line) => sum + line.cantidad, 0)}
                  </div>
                  {selectedEnvio.estado === 'FASE_MONTAJE' && (
                    <div style={{ gridColumn: '1 / -1', marginTop: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                        <strong>Progreso del Montaje:</strong>
                        <span>
                          {selectedEnvio.lines.reduce((sum, line) => sum + (line.is_completed ? line.cantidad : 0), 0)} / {selectedEnvio.lines.reduce((sum, line) => sum + line.cantidad, 0)} unidades completadas ({Math.round((selectedEnvio.lines.reduce((sum, line) => sum + (line.is_completed ? line.cantidad : 0), 0) / Math.max(1, selectedEnvio.lines.reduce((sum, line) => sum + line.cantidad, 0))) * 100)}%)
                        </span>
                      </div>
                      <div style={{ width: '100%', backgroundColor: 'var(--bg-secondary)', borderRadius: '6px', height: '14px', overflow: 'hidden', border: '1px solid var(--border-color)', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)' }}>
                        <div 
                          style={{ 
                            height: '100%', 
                            backgroundColor: '#22c55e', 
                            width: `${Math.round((selectedEnvio.lines.reduce((sum, line) => sum + (line.is_completed ? line.cantidad : 0), 0) / Math.max(1, selectedEnvio.lines.reduce((sum, line) => sum + line.cantidad, 0))) * 100)}%`,
                            transition: 'width 0.4s ease-out'
                          }} 
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="table-container">
                <table className="table">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Nombre del Producto</th>
                      <th style={{ textAlign: 'center' }}>Cantidad</th>
                      {selectedEnvio.estado === 'FASE_MONTAJE' && (
                        <>
                          <th style={{ textAlign: 'center' }}>Progreso</th>
                          <th style={{ textAlign: 'center' }}>Acciones</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEnvio.lines.map((line) => (
                      <tr key={line.id}>
                        <td><strong>{line.sku}</strong></td>
                        <td>{line.nombre_producto}</td>
                        <td style={{ textAlign: 'center' }}>
                          {editingLineId === line.id ? (
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'center' }}>
                              <input
                                type="number"
                                value={editingLineQuantity}
                                onChange={(e) => setEditingLineQuantity(parseInt(e.target.value, 10) || 0)}
                                className="input"
                                style={{ width: '80px', textAlign: 'center' }}
                                min="1"
                                autoFocus
                              />
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => handleSaveLineQuantity(line.id)}
                                title="Guardar"
                              >
                                <Save size={14} />
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={handleCancelEditLine}
                                title="Cancelar"
                              >
                                <X size={14} />
                              </button>
                            </div>
                          ) : (
                            <span>{line.cantidad}</span>
                          )}
                        </td>
                        {selectedEnvio.estado === 'FASE_MONTAJE' && (
                          <td style={{ textAlign: 'center' }}>
                            <button
                              className={`btn btn-sm ${line.is_completed ? 'btn-primary' : 'btn-secondary'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleLineCompletion(line.id, !line.is_completed);
                              }}
                              title={line.is_completed ? "Marcar como pendiente" : "Marcar como completado"}
                              style={{ 
                                display: 'inline-flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                gap: '0.25rem',
                                color: line.is_completed ? 'white' : 'inherit',
                                backgroundColor: line.is_completed ? '#22c55e' : '',
                                borderColor: line.is_completed ? '#22c55e' : ''
                              }}
                            >
                              <CheckCircle2 size={14} />
                            </button>
                          </td>
                        )}
                        {selectedEnvio.estado === 'FASE_MONTAJE' && (
                          <td style={{ textAlign: 'center' }}>
                            {editingLineId !== line.id && (
                              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                                <button
                                  className="btn btn-sm btn-secondary"
                                  onClick={() => handleStartEditLine(line)}
                                  title="Editar cantidad"
                                >
                                  <Edit2 size={14} />
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteEnvioLine(line.id);
                                  }}
                                  title="Eliminar línea"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedEnvio.estado === 'FASE_MONTAJE' && (
                <div style={{ marginTop: '1rem' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddLineModal(true)}
                  >
                    <Plus size={16} /> Añadir Nueva Línea
                  </button>
                </div>
              )}

              {/* BOM de Piezas Necesarias */}
              <div style={{ marginTop: '2rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border-color)' }}>
                <h4 style={{ marginTop: 0, marginBottom: '1rem' }}>BOM de Piezas Necesarias</h4>

                {loadingBOM ? (
                  <Skeleton height="200px" variant="rounded" />
                ) : bomItems.length === 0 ? (
                  <div className="empty-state">
                    <p>No se encontraron piezas necesarias o no hay BOM disponible para los productos del envío.</p>
                  </div>
                ) : (
                  <div className="table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Cód. Pieza</th>
                          <th>Nombre Pieza</th>
                          <th style={{ textAlign: 'center' }}>Cantidad Necesaria</th>
                          <th style={{ textAlign: 'center' }}>Acabados Necesarios</th>
                          <th style={{ textAlign: 'center' }}>Stock Acabado</th>
                          <th style={{ textAlign: 'center' }}>Taller</th>
                          <th style={{ textAlign: 'center' }}>Pedir Fundición</th>
                          <th style={{ textAlign: 'center' }}>Cantidad Fundición</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bomItems.map((item, index) => (
                          <tr key={`${item.cod_pieza}-${index}`}>
                            <td><strong>{item.cod_pieza}</strong></td>
                            <td>{item.nombre_pieza}</td>
                            <td style={{ textAlign: 'center' }}>
                              <strong>{item.cantidad_necesaria}</strong>
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {item.acabados_necesarios && item.acabados_necesarios.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', alignItems: 'center' }}>
                                  {item.acabados_necesarios.map((acabado, idx) => (
                                    <span
                                      key={idx}
                                      style={{
                                        padding: '0.125rem 0.5rem',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        backgroundColor: acabado.acabado === 'PLATA' ? '#d1d5db' : acabado.acabado === 'PVD' ? '#fde047' : '#6366f1',
                                        color: acabado.acabado === 'PLATA' ? '#1f2937' : acabado.acabado === 'PVD' ? '#78350f' : '#ffffff',
                                      }}
                                      title={`${acabado.acabado}: ${acabado.cantidad} unidades`}
                                    >
                                      {acabado.acabado}: {acabado.cantidad}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                '-'
                              )}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {item.stock_acabado ? <CheckCircle2 size={16} strokeWidth={1.5} /> : ''}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {item.stock_taller ? <CheckCircle2 size={16} strokeWidth={1.5} /> : ''}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {item.pedir_fundicion ? <CheckCircle2 size={16} strokeWidth={1.5} /> : ''}
                            </td>
                            <td style={{ textAlign: 'center' }}>
                              {item.cantidad_fundicion > 0 ? (
                                <strong style={{ color: 'var(--error)' }}>
                                  {item.cantidad_fundicion}
                                </strong>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    handlePrintPDF(selectedEnvio.id);
                  }}
                >
                  <FileText size={16} /> Imprimir PDF
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    handlePrintLabelsDirect(selectedEnvio.id);
                  }}
                >
                  <Printer size={16} /> Imprimir Zebra
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    handlePrintLabels(selectedEnvio.id);
                  }}
                >
                  <Tag size={16} /> Etiquetas ZPL
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    handlePrintLabelsPDF(selectedEnvio.id);
                  }}
                >
                  <FileText size={16} /> Etiquetas PDF
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleDownloadAmphoraCSV(selectedEnvio.id);
                  }}
                >
                  <FileSpreadsheet size={16} /> CSV Amphora
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowDetailModal(false);
                    handleDownloadCommercialInvoice(selectedEnvio.id);
                  }}
                >
                  <FileText size={16} /> Factura Comercial
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDetailModal(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal para añadir nueva línea */}
        {showAddLineModal && selectedEnvio && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1001,
            }}
            onClick={() => {
              setShowAddLineModal(false);
              setSelectedVariant(null);
              setProductSearchQuery('');
              setSearchResults([]);
              setNewLineQuantity(1);
            }}
          >
            <div
              style={{
                backgroundColor: 'var(--bg-primary)',
                padding: '2rem',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                border: '1px solid var(--border-color)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0 }}>Añadir Nueva Línea</h3>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => {
                    setShowAddLineModal(false);
                    setSelectedVariant(null);
                    setProductSearchQuery('');
                    setSearchResults([]);
                    setNewLineQuantity(1);
                  }}
                >
                  <X size={16} />
                </button>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Buscar Producto en Shopify
                  {searchingProducts && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      (Buscando...)
                    </span>
                  )}
                </label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    value={productSearchQuery}
                    onChange={(e) => setProductSearchQuery(e.target.value)}
                    className="input"
                    style={{ flex: 1 }}
                    placeholder="Escribe para buscar por nombre o SKU..."
                    autoFocus
                  />
                </div>
                {productSearchQuery.trim() && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                    Los resultados aparecerán automáticamente mientras escribes
                  </div>
                )}
              </div>

              {searchResults.length > 0 && (
                <div style={{ marginBottom: '1.5rem', maxHeight: '300px', overflowY: 'auto' }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem' }}>Resultados:</label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                    {searchResults.map((variant) => (
                      <div
                        key={variant.id}
                        style={{
                          padding: '0.75rem',
                          border: selectedVariant?.id === variant.id ? '2px solid var(--accent)' : '1px solid var(--border-color)',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: selectedVariant?.id === variant.id ? 'var(--bg-secondary)' : 'transparent',
                        }}
                        onClick={() => setSelectedVariant(variant)}
                      >
                        <div style={{ fontWeight: 'bold' }}>{variant.product_title}</div>
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                          {variant.title !== 'Default Title' ? variant.title : 'Sin variante'}
                        </div>
                        <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                          SKU: <strong>{variant.sku}</strong>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedVariant && (
                <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Producto Seleccionado:</div>
                  <div>{selectedVariant.product_title} - {selectedVariant.title !== 'Default Title' ? selectedVariant.title : 'Sin variante'}</div>
                  <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>SKU: {selectedVariant.sku}</div>
                </div>
              )}

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem' }}>Cantidad</label>
                <input
                  type="number"
                  value={newLineQuantity}
                  onChange={(e) => setNewLineQuantity(parseInt(e.target.value, 10) || 1)}
                  className="input"
                  style={{ width: '100%' }}
                  min="1"
                />
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddLineModal(false);
                    setSelectedVariant(null);
                    setProductSearchQuery('');
                    setSearchResults([]);
                    setNewLineQuantity(1);
                  }}
                >
                  Cancelar
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleAddLine}
                  disabled={!selectedVariant || newLineQuantity <= 0}
                >
                  Añadir Línea
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de movimientos de stock */}
        {showStockMovementsModal && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onClick={() => setShowStockMovementsModal(false)}
          >
            <div
              style={{
                backgroundColor: 'var(--bg-primary)',
                padding: '2rem',
                borderRadius: '8px',
                maxWidth: '900px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                border: '1px solid var(--border-color)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h3 style={{ marginTop: 0 }}>Movimientos de Stock - Envío Marcado como Enviado</h3>
                <button
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowStockMovementsModal(false)}
                >
                  <X size={16} />
                </button>
              </div>

              {stockMovements.length === 0 && failedMovements.length === 0 ? (
                <p>No se realizaron movimientos de stock.</p>
              ) : (
                <>
                  {/* Tabla de movimientos exitosos */}
                  {stockMovements.length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Movimientos Realizados</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Código Pieza</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Nombre Pieza</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Ubicación</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>Requerido</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>Descontado</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>Stock Antes</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>Stock Después</th>
                              <th style={{ padding: '0.75rem', textAlign: 'center', fontWeight: 'bold' }}>Estado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stockMovements.map((movement, index) => {
                              const isPartial = movement.qty_deducted < movement.qty_needed;
                              return (
                                <tr
                                  key={index}
                                  style={{
                                    borderBottom: '1px solid var(--border-color)',
                                    backgroundColor: isPartial ? 'rgba(245, 158, 11, 0.1)' : 'transparent'
                                  }}
                                >
                                  <td style={{ padding: '0.75rem' }}>{movement.cod_pieza}</td>
                                  <td style={{ padding: '0.75rem' }}>{movement.nombre_pieza}</td>
                                  <td style={{ padding: '0.75rem' }}>
                                    {movement.location_code === 'ACABADO_PLATA' && 'Acabado Plata'}
                                    {movement.location_code === 'ACABADO_PVD' && 'Acabado PVD'}
                                    {movement.location_code === 'ACABADO_DLC' && 'Acabado DLC'}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>{movement.qty_needed}</td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                    {movement.qty_deducted}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right' }}>{movement.stock_before}</td>
                                  <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                    {movement.stock_after}
                                  </td>
                                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                    {isPartial ? (
                                      <span style={{
                                        color: '#f59e0b',
                                        fontWeight: 'bold',
                                        fontSize: '0.85rem'
                                      }}>
                                        ⚠️ Parcial
                                      </span>
                                    ) : (
                                      <span style={{
                                        color: '#10b981',
                                        fontWeight: 'bold',
                                        fontSize: '0.85rem'
                                      }}>
                                        ✅ Completo
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Tabla de movimientos fallidos */}
                  {failedMovements.length > 0 && (
                    <div style={{ marginTop: failedMovements.length > 0 && stockMovements.length > 0 ? '2rem' : '0' }}>
                      <h4 style={{ marginBottom: '1rem', color: '#ef4444', fontWeight: 'bold' }}>⚠️ Movimientos No Realizados ({failedMovements.length})</h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid var(--border-color)', backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Código Pieza</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Nombre Pieza</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Ubicación</th>
                              <th style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>Requerido</th>
                              <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: 'bold' }}>Razón</th>
                            </tr>
                          </thead>
                          <tbody>
                            {failedMovements.map((failed, index) => (
                              <tr
                                key={index}
                                style={{
                                  borderBottom: '1px solid var(--border-color)',
                                  backgroundColor: 'rgba(239, 68, 68, 0.05)'
                                }}
                              >
                                <td style={{ padding: '0.75rem' }}>{failed.cod_pieza}</td>
                                <td style={{ padding: '0.75rem' }}>{failed.nombre_pieza}</td>
                                <td style={{ padding: '0.75rem' }}>
                                  {failed.location_code === 'ACABADO_PLATA' && 'Acabado Plata'}
                                  {failed.location_code === 'ACABADO_PVD' && 'Acabado PVD'}
                                  {failed.location_code === 'ACABADO_DLC' && 'Acabado DLC'}
                                  {!['ACABADO_PLATA', 'ACABADO_PVD', 'ACABADO_DLC'].includes(failed.location_code) && failed.location_code}
                                </td>
                                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: 'bold' }}>
                                  {failed.qty_needed}
                                </td>
                                <td style={{ padding: '0.75rem', color: '#ef4444', fontWeight: 'bold' }}>
                                  ❌ {failed.reason}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowStockMovementsModal(false)}
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

